import React, { useState, useEffect, useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { dataService } from "../../../Services/data/data.service";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Typography, Box, Tooltip } from "@mui/material";
import { Plus, Pencil, Infinity, Users, Factory, EyeOff, UserCog } from "lucide-react";
import { useTranslation } from 'react-i18next';
import UserCrudDialog from "./UserCrudDialog";
import CompanyCrudDialog from "./CompanyCrudDialog";

import { Roles } from "../../../Config/constants";
import UserEditDialog from "./UserEditDialog";
import { Spin } from "react-cssfx-loading";
import UserLimitReachedDialog from "../../Shared/Dialogs/UserLimitReachedDialog";
import SubscriberAdminsDialog from "../../Shared/Dialogs/SubscriberAdminsDialog";
import EmptyStateCard from "../../Shared/EmptyStateCard";
import Chip from "../../Shared/ui/Chip";
import Button from "../../Shared/Buttons/Button";
import HeaderBar from "../../Shared/Header/HeaderBar";
import HeaderTitle from "../../Shared/Header/HeaderTitle";
import HeaderEnd from "../../Shared/Header/HeaderEnd";

toast.configure();

export default function CompanySettingsPage(props) {
  const classes = useStyles();
  const { t } = useTranslation();
  const [userIsAdmin, setUserIsAdmin] = useState(false);
  const [userCompanies, setUserCompanies] = useState(null);
  const [roles, setRoles] = useState(null);
  const [loading, setLoading] = useState(true);

  // Dialogs
  const [crudDialogOpen, setCrudDialogOpen] = useState(false);
  const [companyCrudDialogOpen, setCompanyCrudDialogOpen] = useState(false);
  const [userLimitReachedDialogOpen, setUserLimitReachedDialogOpen] = useState(false);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [companyId, setCompanyId] = useState(null);
  const [userToEdit, setUserToEdit] = useState(null);

  const [companyToEdit, setCompanyToEdit] = useState(null);

  const [validSubscription, setValidSubscription] = useState(false);
  const [generalPageError, setGeneralPageError] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [companyCount, setCompanyCount] = useState(0);

  // Subscription administrators (only loaded for subscription admins)
  const [admins, setAdmins] = useState([]);
  const [adminsDialogOpen, setAdminsDialogOpen] = useState(false);

  // Seats = unique emails of enabled users in active companies + subscription admins (mirrors the backend count)
  const userCount = useMemo(() => {
    const emails = new Set(admins.map((admin) => admin.emailAddress));
    (userCompanies || []).forEach((company) => {
      if (!company.isActive) return;
      company.users.forEach((user) => {
        if (user.enabled && user.emailAddress) emails.add(user.emailAddress);
      });
    });
    return emails.size;
  }, [userCompanies, admins]);

  useEffect(() => {
    //Promise is used so functions will happen together.
    //When all are finished --> setLoading false
    Promise.all([dataService.getAllRoles()])
      .then((results) => {
        var roleResults = results[0];
        if (roleResults) {
          setRoles(roleResults);
        }
      })
      .catch((error) => {
        toast.error(t('companySettings.fetchError'), { position: toast.POSITION.TOP_CENTER, autoClose: 2000 });
        console.log("Error: ", error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [props.Login.userid]);

  const fetchCompanies = () => {
    if (props.Login.subscriberUniqueId !== null) {
      dataService
        .getCompaniesAndUsersBySubscriberUniqueId(props.Login.subscriberUniqueId)
        .then((result) => {
          if (result) {
            let tempCompanyCount = 0;

            result.forEach((company) => {
              if (company.isActive) {
                tempCompanyCount++;
              }

              if (company.users.find((x) => x.id === props.Login.userid)) {
                company.isHidden = company.users.find((x) => x.id === props.Login.userid).companyHidden;
              } else {
                company.isHidden = false;
              }
            });

            setCompanyCount(tempCompanyCount);
            setUserCompanies(result);
          }
        })
        .catch((error) => {
          toast.error(t('companySettings.fetchError'), { position: toast.POSITION.TOP_CENTER, autoClose: 2000 });
          console.log("Error: ", error);
        });
    } else {
      dataService
        .getUserAndCompanyData(props.Login.userid)
        .then((result) => {
          if (result) {
            let tempCompanyCount = 0;

            result.forEach((company) => {
              if (company.isActive) {
                tempCompanyCount++;
              }
            });

            setCompanyCount(tempCompanyCount);
            setUserCompanies(result);
          }
        })
        .catch((error) => {
          toast.error(t('companySettings.fetchError'), { position: toast.POSITION.TOP_CENTER, autoClose: 2000 });
          console.log("Error: ", error);
        });
    }
  };

  // Fetching companies and users
  useEffect(() => {
    if (props.Login.subscriberUniqueId !== null) {
      setUserIsAdmin(true);

      dataService
        .getSubscriptionBySubscriberUniqueId(props.Login.subscriberUniqueId)
        .then((result) => {
          if (result.success) {
            setSubscription(result.subscription);
            setValidSubscription(true);
          } else {
            setGeneralPageError(t('projectReportOverview.noValidSubscriptionError'));
            setValidSubscription(false);
          }
        })
        .catch((error) => {
          toast.error(t('companySettings.invalidSubscriptionToast'), { position: toast.POSITION.TOP_CENTER, autoClose: 2000 });
        });

      dataService
        .getSubscriberAdmins(props.Login.subscriberUniqueId)
        .then((result) => {
          if (result && result.success) {
            setAdmins(result.administrators);
          }
        })
        .catch((error) => {
          console.log("Error: ", error);
        });
    } else {
      setUserIsAdmin(false);
      setAdmins([]);
    }

    fetchCompanies();
  }, [props.Login]);

  const handleCreateCompany = () => {
    setCompanyCrudDialogOpen(true);
    setCompanyId(null);
  };
  const closeCompanyCrudDialog = () => {
    setCompanyToEdit(null);
    setCompanyCrudDialogOpen(false);
  };

  const handleCreateUser = (intCompanyId) => {
    // backend check if subscriber has reached user limit
    dataService
      .checkSubscriptionUserLimitByCompanyId(intCompanyId)
      .then((result) => {
        let userLimitReached = result;

        if (userLimitReached) {
          setUserLimitReachedDialogOpen(true);
        } else {
          setCrudDialogOpen(true);
          setCompanyId(intCompanyId);
        }
      })
      .catch((error) => {
        toast.error(t('companySettings.fetchError'), { position: toast.POSITION.TOP_CENTER, autoClose: 2000 });
        console.log("Error: ", error);
      });
  };

  const closeCrudDialog = () => {
    setCrudDialogOpen(false);
  };

  const closeEditDialog = () => {
    setEditDialogOpen(false);
  };

  const nothing = (intUserLimit) => {
    toast.error(t('companySettings.userLimitReached', { limit: intUserLimit }), { position: toast.POSITION.TOP_CENTER, autoClose: 2000 });
  };

  const handleEditCompany = (company) => {
    setCompanyToEdit(company);
    setCompanyCrudDialogOpen(true);
  };

  const handleCompanyCloned = () => {
    fetchCompanies();
    closeCompanyCrudDialog();
  };

  const handleCompanyHidden = (companyId, isHidden) => {
    setUserCompanies((prev) =>
      prev.map((company) =>
        company.id === companyId ? { ...company, isHidden } : company
      )
    );
  };

  const handleEditUser = (intCompanyId, intUserId) => {
    try {
      var arrCompanies = JSON.parse(JSON.stringify(userCompanies));
      var objCompany = arrCompanies.find((x) => x.id === intCompanyId);
      var objUser = objCompany.users.find((x) => x.id === intUserId);
      setUserToEdit(objUser);
      setCompanyId(intCompanyId);
      setEditDialogOpen(true);
    } catch (error) {
      console.log("error ", error);
      toast.error(t('errors.generic'), { position: toast.POSITION.TOP_CENTER, autoClose: 2000 });
    }
  };

  const getSubscriptionCompanyLimit = () => {
    if (subscription.companyLimit) {
      return subscription.companyLimit;
    } else {
      return <Infinity className={classes.infinityIcon} />;
    }
  };

  const getSubscriptionUserLimit = () => {
    if (subscription.userLimit) {
      return subscription.userLimit;
    } else {
      return <Infinity className={classes.infinityIcon} />;
    }
  };

  const isCompanyLimitReached = () => {
    if (subscription && subscription.companyLimit) {
      return companyCount >= subscription.companyLimit;
    } else {
      return false;
    }
  };

  const isUserLimitReached = () => {
    if (subscription && subscription.userLimit) {
      return userCount >= subscription.userLimit;
    } else {
      return false;
    }
  };

  const formatActiveCell = (isActive) => {
    return isActive ? (
      <Chip variant={"success"} size="small">
        {t('common.active')}
      </Chip>
    ) : (
      <Chip variant={"default"} size="small">
        {t('common.inactive')}
      </Chip>
    );
  };

  return (
    <React.Fragment>
      {loading && (
        <div className={classes.loadingChart}>
          <Spin width="100px" height="100px" color="var(--color-primary)"></Spin>
        </div>
      )}
      <Box className="page-container">
        <HeaderBar>
          <HeaderTitle>{t('companySettings.manageCompanies')}</HeaderTitle>
          <HeaderEnd>
            {userIsAdmin && !validSubscription && (
              <div className={classes.noSubscriptionChip}>
                <p>{t('companySettings.noActiveSubscription')}</p>
              </div>
            )}
            {subscription && (
              <div className={classes.subscriptionInformationContainer}>
                <div className={classes.subscriptionCompanyCountChip}>
                  <Factory className={classes.chip__icons} />
                  <p className={classes.chip__text}>
                    {companyCount}/{getSubscriptionCompanyLimit()}
                  </p>
                </div>
                <div className={classes.subscriptionUserCountChip}>
                  <Users className={classes.chip__icons} />
                  <p className={classes.chip__text}>
                    {userCount}/{getSubscriptionUserLimit()}
                  </p>
                </div>
              </div>
            )}
            {userIsAdmin && validSubscription && (
              <Button size="small" variant="light" startIcon={<UserCog />} onClick={() => setAdminsDialogOpen(true)}>
                {t('subscriberOverview.manageAdministrators')}
              </Button>
            )}
            {userIsAdmin && validSubscription && (
              <Tooltip title={isCompanyLimitReached() ? t('companySettings.companyLimitReachedTooltip') : ""} placement="top" arrow>
                <button className={classes.tableManagementContainer__primaryIconButton} disabled={isCompanyLimitReached()} onClick={() => handleCreateCompany()}>
                  <Plus className={classes.iconButton__icon} />
                  {t('companySettings.createCompany')}
                </button>
              </Tooltip>
            )}
          </HeaderEnd>
        </HeaderBar>
        <Box>
          <div className={classes.container}>
            {userCompanies && userCompanies.length == 0 && <EmptyStateCard title={t('companySettings.notInAnyCompany')} />}
            {userCompanies && userCompanies.length > 0 && (
              <Box sx={{ borderRadius: "8px", maxWidth: "100%", marginRight: "auto", marginLeft: "auto" }}>
                {userCompanies.map((company, companyIndex) => {
                  return (
                    <Box key={"companyBox_" + companyIndex} sx={{ width: "100%", marginBottom: "24px" }}>
                      <Box className={classes.companyHeader}>
                        <Typography variant="h5" className={classes.projectTitle}>
                          {company.name}
                        </Typography>
                        <div className={classes.companyHeader__end}>
                          {company.isHidden && <Chip startAdornment={<EyeOff />}>{t('companySettings.hidden')}</Chip>}
                          {company.isActive ? <Chip variant="success">{t('common.active')}</Chip> : <Chip variant="danger">{t('common.inactive')}</Chip>}
                          <Chip>
                            <p>{company.currencyName}</p>
                            <p style={{ marginLeft: "4px" }}>{company.currencySymbol}</p>
                          </Chip>
                          {userIsAdmin && validSubscription && (
                            <button onClick={() => handleEditCompany(company)} className={classes.iconButton}>
                              <Pencil className={classes.iconButton__icon} />
                            </button>
                          )}
                          <Button size="small" startIcon={<Plus />} disabled={!company.isActive || (userIsAdmin && !validSubscription)} onClick={() => handleCreateUser(company.id)}>
                            {t('companySettings.createUser')}
                          </Button>
                        </div>
                      </Box>
                      <div className={classes.tableContainer}>
                        <table className={classes.table}>
                          <thead className={classes.table__header}>
                            <tr>
                              <th style={{ width: "56px", textAlign: "center" }}>#</th>
                              <th style={{ width: "30%" }}>{t('common.name')}</th>
                              <th>{t('auth.email')}</th>
                              <th style={{ width: "140px" }}>{t('common.active')}</th>
                              <th style={{ width: "48px" }} />
                            </tr>
                          </thead>
                          <tbody className={classes.table__body}>
                            {company.users.length > 0 ? (
                              [...company.users]
                                .sort((a, b) => a.firstname.localeCompare(b.firstname))
                                .map((row, rowIndex) => (
                                  <tr className={classes.table__row} key={company.id + "_userRow_" + rowIndex}>
                                    <td style={{ textAlign: "center" }}>{rowIndex + 1}</td>
                                    <td>
                                      {row.firstname} {row.name}
                                    </td>
                                    <td>{row.emailAddress}</td>
                                    <td>{formatActiveCell(row.enabled)}</td>
                                    <td>
                                      <div className={classes.buttonContainer}>
                                        <button className={classes.iconButton} onClick={() => handleEditUser(company.id, row.id)}>
                                          <Pencil className={classes.iconButton__icon} />
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                ))
                            ) : (
                              <tr>
                                <td colSpan="5" style={{ textAlign: "center", padding: "32px" }}>
                                  {t('companySettings.noUsersFound')}
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </Box>
                  );
                })}
              </Box>
            )}
          </div>
        </Box>
      </Box>
      {companyCrudDialogOpen && (
        <CompanyCrudDialog
          dialogOpen={companyCrudDialogOpen}
          closeDialog={closeCompanyCrudDialog}
          roles={roles.filter((x) => x.id !== Roles.ADMIN)}
          companyId={companyId}
          companyToEdit={companyToEdit}
          userId={props.Login.userid}
          subscriberUniqueId={props.Login.subscriberUniqueId}
          onCompanyHidden={handleCompanyHidden}
          onCompanyCloned={handleCompanyCloned}
          {...props}
        ></CompanyCrudDialog>
      )}

      {crudDialogOpen && (
        <UserCrudDialog dialogOpen={crudDialogOpen} closeDialog={closeCrudDialog} roles={roles.filter((x) => x.id !== Roles.ADMIN)} companyId={companyId} userId={props.Login.userid}></UserCrudDialog>
      )}
      {editDialogOpen && (
        <UserEditDialog
          dialogOpen={editDialogOpen}
          closeDialog={closeEditDialog}
          roles={roles.filter((x) => x.id !== Roles.ADMIN)}
          companyId={companyId}
          userToEdit={userToEdit}
          userId={props.Login.userid}
          userCompanies={userCompanies}
        ></UserEditDialog>
      )}
      <UserLimitReachedDialog dialogOpen={userLimitReachedDialogOpen} closeDialog={() => setUserLimitReachedDialogOpen(false)} />
      {userIsAdmin && (
        <SubscriberAdminsDialog
          open={adminsDialogOpen}
          onClose={() => setAdminsDialogOpen(false)}
          subscriberUniqueId={props.Login.subscriberUniqueId}
          currentUserId={props.Login.userid}
          onAdminsChanged={(response) => setAdmins(response.administrators)}
        />
      )}
    </React.Fragment>
  );
}

const useStyles = makeStyles({
  header: {
    width: "100%",
    height: "40px",

    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",

    marginBottom: "16px",
  },

  header__right: {
    display: "flex",
    gap: "16px",
    alignItems: "center",
  },

  subscriptionInformationContainer: {
    display: "flex",
    gap: "8px",
    alignItems: "center",
  },

  subscriptionCompanyCountChip: {
    height: "24px",
    padding: "4px 8px 4px 6px",

    display: "flex",
    alignItems: "center",
    gap: "4px",

    border: "1px solid #DCDCDC",
    borderRadius: "12px",
    backgroundColor: "#EDEDED",
  },

  subscriptionUserCountChip: {
    height: "24px",
    padding: "4px 8px 4px 6px",

    display: "flex",
    alignItems: "center",
    gap: "4px",

    border: "1px solid #DCDCDC",
    borderRadius: "12px",
    backgroundColor: "#EDEDED",
  },

  noSubscriptionChip: {
    height: "28px",
    padding: "4px 8px",

    display: "flex",
    alignItems: "center",

    color: "#FDF3F3",
    fontFamily: "Lato, sans-serif",
    fontSize: "14px",

    backgroundColor: "#D84949",

    border: "1px solid #FBE5E5",
    borderRadius: "14px",
  },

  chip__icons: {
    width: "16px",
    height: "16px",
    color: "#0C0E29",
  },

  chip__text: {
    display: "flex",
    alignItems: "center",
    gap: "2px",

    color: "#0C0E29",
    fontFamily: "Lato, sans-serif",
    fontSize: "16px",
  },

  loadingChart: {
    height: "100vh",
    width: "100%",
    top: "0px",
    zIndex: "999",
    position: "fixed",
    borderRadius: "8px",
    backgroundColor: "rgba(156,156,156,0.5)",
    display: "flex",
    alignContent: "center",
    alignItems: "center",
    justifyContent: "center",
  },

  companyHeader: {
    width: "100%",
    backgroundColor: "var(--color-bg-card)",
    border: "1px solid var(--color-border-medium)",
    color: "var(--color-text-primary)",
    borderRadius: "12px",
    display: "flex",
    justifyContent: "space-between",
    padding: "16px",
    marginBottom: "8px",
  },

  companyHeader__end: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
  },

  chip: {
    height: "24px",

    display: "flex",
    gap: "4px",
    alignItems: "center",

    color: "#7C7C7C",
    fontFamily: "Lato, sans-serif",
    fontSize: "14px",

    backgroundColor: "#EDEDED",

    borderRadius: "12px",
    padding: "4px 12px",
  },

  chip__success: {
    height: "24px",

    display: "flex",
    gap: "4px",
    alignItems: "center",

    color: "#3AB037",
    fontFamily: "Lato, sans-serif",
    fontSize: "14px",

    backgroundColor: "#F3FBF2",

    borderRadius: "12px",
    padding: "4px 12px",
  },

  chip__danger: {
    height: "24px",

    display: "flex",
    gap: "4px",
    alignItems: "center",

    color: "#C73B3B",
    fontFamily: "Lato, sans-serif",
    fontSize: "14px",

    backgroundColor: "#FDF3F3",

    borderRadius: "12px",
    padding: "4px 12px",
  },

  buttonContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
  },

  iconButton: {
    height: "32px",
    width: "32px",

    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    borderRadius: "4px",
    border: "none",

    backgroundColor: "#F8F8F8",

    "&:hover": {
      backgroundColor: "#EDEDED",
      cursor: "pointer",
    },
  },

  iconButton__icon: {
    height: "20px",
    width: "20px",

    display: "block",
  },

  tableContainer: {
    width: "100%",
    overflowX: "auto",
  },

  table: {
    width: "100%",
    tableLayout: "fixed",
    borderCollapse: "collapse",
    borderSpacing: "0",

    backgroundColor: "#FFFFFF",
    borderRadius: "8px",
  },

  table__header: {
    backgroundColor: "#DFDFDF",
    borderRadius: "8px 8px 0px 0px",

    "& th": {
      height: "36px",
      padding: "0px 16px",
      textAlign: "left",
      fontSize: "16px",
      fontWeight: "normal",
      color: "#0C0E29",

      "&:first-child": {
        borderTopLeftRadius: "8px",
      },

      "&:last-child": {
        borderTopRightRadius: "8px",
        width: "0px",
        padding: "0px",
      },
    },
  },

  table__body: {
    "& tr": {
      borderBottom: "1px solid #EDEDED",

      "&:last-child": {
        borderBottom: "none",

        "& td": {
          "&:first-child": {
            borderBottomLeftRadius: "8px",
          },

          "&:last-child": {
            borderBottomRightRadius: "8px",
          },
        },
      },

      "& td": {
        height: "48px",
        padding: "0px 16px",
        textAlign: "left",
        fontSize: "16px",
        fontWeight: "normal",
        color: "#0C0E29",

        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        maxWidth: 200,

        borderRight: "1px solid #EDEDED",

        "&:last-child": {
          borderRight: "none",
          width: "fit-content",
          padding: "8px",
        },
      },
    },
  },

  table__row: {
    backgroundColor: "#FFFFFF !important",
  },

  projectTitle: {
    padding: "0px",
  },

  tableManagementContainer__primaryIconButton: {
    height: "32px",
    padding: "0 12px",
    paddingLeft: "6px",

    border: "none",
    borderRadius: "4px",

    fontSize: "16px",
    color: "white",
    backgroundColor: "var(--color-primary)",

    display: "flex",
    gap: "4px",
    alignItems: "center",

    transition: "all 0.2s ease-in-out",

    "&:hover": {
      backgroundColor: "#a77d4f",
      color: "white",
      cursor: "pointer",
    },

    "&:disabled": {
      backgroundColor: "#DCDCDC",
      color: "#BDBDBD",
      cursor: "not-allowed",

      "&:hover": {
        backgroundColor: "#DCDCDC",
        color: "#BDBDBD",
        cursor: "not-allowed",
      },
    },
  },
  infinityIcon: {
    width: "16px",
    height: "16px",
    color: "#0C0E29",
  },
});